import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { io, Socket } from "socket.io-client";
import toast from "react-hot-toast";
import { friendDuelAPI } from "../api";
import { useLang } from "../context/LangContext";

const SOCKET_BASE =
  process.env.REACT_APP_SOCKET_URL ||
  (process.env.REACT_APP_BASE_URL || "http://localhost:3003/api").replace(
    /\/api$/,
    "",
  );

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;
const DEFAULT_IMG = "https://avtomind.uz/storage/photos/default.webp";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Choice {
  index: number;
  text_uz: string;
  text_oz: string;
  text_ru: string;
}

interface Question {
  id: number;
  question_uz: string;
  question_oz: string;
  question_ru: string;
  photo_url?: string | null;
  choices: Choice[];
}

interface AnswerResult {
  isCorrect: boolean;
  correctIndex: number;
}

interface ResultData {
  player1Score: number;
  player2Score: number;
  winner: string;
  isWinner: boolean;
}

type Step = "inviter_waiting" | "playing" | "waiting_finish" | "result";

// location.state shapes
interface InviterState {
  role: "inviter";
  inviteId: string;
  friendName: string;
  friendPhoto: string;
  questionCount: number;
  expiresAt: string;
}

interface InviteeState {
  role: "invitee";
  sessionId: string;
  questions: Question[];
  opponentName: string;
  opponentPhoto: string;
  totalQuestions: number;
}

type LocationState = InviterState | InviteeState;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const photoUrl = (photo: string) => {
  if (!photo) return DEFAULT_IMG;
  if (photo.startsWith("http")) return photo;
  return `${IMG_BASE}/${photo}`;
};

// ─── Component ───────────────────────────────────────────────────────────────

const FriendDuelPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLang();

  const state = location.state as LocationState | null;

  // ─── Refs ──────────────────────────────────────────────────────────────────
  const socketRef = useRef<Socket | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const questionsRef = useRef<Question[]>([]);
  const currentRef = useRef(0);
  const answeredRef = useRef<Record<number, number>>({});

  // ─── State ─────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>(() => {
    if (!state) return "inviter_waiting";
    return state.role === "invitee" ? "playing" : "inviter_waiting";
  });

  const [questions, setQuestions] = useState<Question[]>(() =>
    state?.role === "invitee" ? state.questions : [],
  );

  const [opponentName, setOpponentName] = useState(() =>
    state?.role === "invitee" ? state.opponentName
    : state?.role === "inviter" ? state.friendName
    : "",
  );

  const [opponentPhoto, setOpponentPhoto] = useState(() =>
    state?.role === "invitee" ? state.opponentPhoto
    : state?.role === "inviter" ? state.friendPhoto
    : "",
  );

  // inviter waiting countdown
  const [timeLeft, setTimeLeft] = useState(0);

  // playing
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState<Record<number, number>>({});
  const [answerResults, setAnswerResults] = useState<Record<number, AnswerResult>>({});
  const [myProgress, setMyProgress] = useState({ answered: 0, correct: 0 });
  const [opponentProgress, setOpponentProgress] = useState({ answered: 0, correct: 0 });
  const [opponentFinishedMsg, setOpponentFinishedMsg] = useState<string | null>(null);

  // result
  const [resultData, setResultData] = useState<ResultData | null>(null);

  // ─── Keep refs in sync ─────────────────────────────────────────────────────
  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  // ─── Redirect if no state ─────────────────────────────────────────────────
  useEffect(() => {
    if (!state) {
      navigate("/custom", { replace: true });
    }
  }, [state, navigate]);

  // ─── Countdown timer (inviter_waiting) ────────────────────────────────────
  useEffect(() => {
    if (step !== "inviter_waiting" || state?.role !== "inviter") return;
    const expiresAt = state.expiresAt;

    const calc = () =>
      Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));

    setTimeLeft(calc());

    const interval = setInterval(() => {
      const left = calc();
      setTimeLeft(left);
      if (left <= 0) {
        clearInterval(interval);
        toast.error("Taklif muddati tugadi");
        navigate(-1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [step, state, navigate]);

  // ─── Socket setup ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!state) return;

    const token = localStorage.getItem("token");
    const socket = io(`${SOCKET_BASE}/friend-duel`, {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    // ── Inviter waiting events ───────────────────────────────────────────
    socket.on(
      "friend_game_started",
      (data: {
        sessionId: string;
        totalQuestions: number;
        questions: Question[];
        opponentName: string;
        opponentPhoto: string;
      }) => {
        sessionIdRef.current = data.sessionId;
        questionsRef.current = data.questions;
        answeredRef.current = {};

        setQuestions(data.questions);
        setOpponentName(data.opponentName);
        setOpponentPhoto(data.opponentPhoto);
        setCurrent(0);
        currentRef.current = 0;
        setAnswered({});
        setAnswerResults({});
        setMyProgress({ answered: 0, correct: 0 });
        setOpponentProgress({ answered: 0, correct: 0 });
        setOpponentFinishedMsg(null);
        setStep("playing");
      },
    );

    socket.on("friend_invite_declined", ({ message }: { message: string }) => {
      toast.error(message || "Do'stingiz taklifni rad etdi");
      navigate(-1);
    });

    // ── Game events ──────────────────────────────────────────────────────
    socket.on(
      "friend_answer_result",
      (data: {
        isCorrect: boolean;
        correctIndex: number;
        alreadyAnswered?: boolean;
      }) => {
        if (data.alreadyAnswered) return;

        const idx = currentRef.current;
        const qs = questionsRef.current;

        setAnswerResults((prev) => ({
          ...prev,
          [idx]: { isCorrect: data.isCorrect, correctIndex: data.correctIndex },
        }));

        setMyProgress((prev) => ({
          answered: prev.answered + 1,
          correct: prev.correct + (data.isCorrect ? 1 : 0),
        }));

        setTimeout(() => {
          if (idx >= qs.length - 1) {
            socket.emit("friend_finish_game", {
              sessionId: sessionIdRef.current,
            });
            setStep("waiting_finish");
          } else {
            const next = idx + 1;
            setCurrent(next);
            currentRef.current = next;
          }
        }, 1200);
      },
    );

    socket.on(
      "friend_opponent_progress",
      (data: { answeredCount: number; correctCount: number }) => {
        setOpponentProgress({
          answered: data.answeredCount,
          correct: data.correctCount,
        });
      },
    );

    socket.on("friend_opponent_disconnected", ({ message }: { message: string }) => {
      toast.success(message || "Do'stingiz uzildi. Siz g'olib!");
      setStep("result");
      setResultData({ player1Score: 0, player2Score: 0, winner: "me", isWinner: true });
    });

    socket.on("friend_opponent_finished", () => {
      setOpponentFinishedMsg("Do'stingiz tugatdi! Natija hisoblanmoqda...");
    });

    socket.on(
      "friend_game_over",
      (data: {
        sessionId: string;
        player1Score: number;
        player2Score: number;
        winner: string;
        isWinner: boolean;
      }) => {
        setResultData(data);
        setStep("result");
      },
    );

    // Set up game state for invitee (game data came from HTTP, socket already connected)
    if (state.role === "invitee") {
      sessionIdRef.current = state.sessionId;
      questionsRef.current = state.questions;
    }

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Before unload warning ─────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (step === "playing" || step === "waiting_finish") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [step]);

  // ─── Localisation ─────────────────────────────────────────────────────────
  const getQ = (q: Question) => {
    if (lang === "ru") return q.question_ru || q.question_oz;
    if (lang === "uz") return q.question_uz || q.question_oz;
    return q.question_oz || q.question_uz;
  };

  const getChoice = (c: Choice) => {
    if (lang === "ru") return c.text_ru || c.text_oz;
    if (lang === "uz") return c.text_uz || c.text_oz;
    return c.text_oz || c.text_uz;
  };

  // ─── Actions ──────────────────────────────────────────────────────────────
  const handleAnswer = useCallback((choiceIndex: number) => {
    const idx = currentRef.current;
    if (answeredRef.current[idx] !== undefined) return;
    answeredRef.current[idx] = choiceIndex;

    const q = questionsRef.current[idx];
    if (!q || !sessionIdRef.current || !socketRef.current) return;

    setAnswered((prev) => ({ ...prev, [idx]: choiceIndex }));
    socketRef.current.emit("friend_submit_answer", {
      sessionId: sessionIdRef.current,
      questionId: q.id,
      choiceIndex,
    });
  }, []);

  const cancelInvite = useCallback(async () => {
    if (state?.role !== "inviter") return;
    try {
      await friendDuelAPI.cancel(state.inviteId);
    } catch {}
    socketRef.current?.disconnect();
    navigate(-1);
  }, [state, navigate]);

  // ─── Derived ──────────────────────────────────────────────────────────────
  const q = questions[current];
  const hasAnswered = answered[current] !== undefined;
  const currentResult = answerResults[current];

  // ════════════════════════════════════════════════════════════════════════════
  // EKRAN 3 — INVITER WAITING
  // ════════════════════════════════════════════════════════════════════════════
  if (step === "inviter_waiting" && state?.role === "inviter") {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0A0E1A",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 16px",
          flexDirection: "column",
        }}>
        {/* Friend avatar */}
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            overflow: "hidden",
            border: "3px solid rgba(59,130,246,.5)",
            marginBottom: 16,
            background: "rgba(59,130,246,.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
          <img
            src={photoUrl(state.friendPhoto)}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => {
              e.currentTarget.src = DEFAULT_IMG;
            }}
          />
        </div>

        <div
          style={{ fontWeight: 900, fontSize: 18, marginBottom: 6, textAlign: "center" }}>
          {state.friendName}
        </div>

        <div
          style={{
            color: "#9ca3af",
            fontSize: 14,
            marginBottom: 32,
            textAlign: "center",
          }}>
          Taklif qabul qilinishini kutmoqda...
        </div>

        {/* Countdown */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(245,158,11,.1)",
            border: "1px solid rgba(245,158,11,.3)",
            borderRadius: 12,
            padding: "12px 24px",
            marginBottom: 32,
          }}>
          <span style={{ fontSize: 20 }}>⏱</span>
          <span
            style={{ fontWeight: 900, fontSize: 22, color: "#F59E0B" }}>
            {formatTime(timeLeft)}
          </span>
        </div>

        <div
          style={{
            color: "#6b7280",
            fontSize: 13,
            marginBottom: 32,
            textAlign: "center",
          }}>
          {state.questionCount} ta savol • ~
          {Math.ceil(state.questionCount / 10)} daqiqa
        </div>

        {/* Spinner */}
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: "3px solid rgba(59,130,246,.2)",
            borderTop: "3px solid #3B82F6",
            animation: "spin 1s linear infinite",
            marginBottom: 32,
          }}
        />

        <button
          onClick={cancelInvite}
          style={{
            padding: "14px 32px",
            background: "rgba(239,68,68,.08)",
            border: "1px solid rgba(239,68,68,.3)",
            borderRadius: 14,
            color: "#f87171",
            fontWeight: 800,
            fontSize: 14,
            cursor: "pointer",
          }}>
          Bekor qilish
        </button>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // EKRAN 4 — PLAYING
  // ════════════════════════════════════════════════════════════════════════════
  if (step === "playing" && q) {
    const totalQ = questions.length;
    const progressPct = totalQ > 0 ? (myProgress.answered / totalQ) * 100 : 0;
    const oppProgressPct =
      totalQ > 0 ? (opponentProgress.answered / totalQ) * 100 : 0;

    return (
      <div style={{ minHeight: "100vh", background: "#0A0E1A", color: "#fff" }}>
        {/* Header */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: "rgba(10,14,26,0.95)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid rgba(31,45,69,0.5)",
          }}>
          <div
            style={{
              maxWidth: 620,
              margin: "0 auto",
              padding: "10px 16px",
            }}>
            {/* Players row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}>
              {/* Me */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "#3B82F6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 900,
                    fontSize: 12,
                    flexShrink: 0,
                  }}>
                  S
                </div>
                <div>
                  <div style={{ fontSize: 9, color: "#6b7280", fontWeight: 800 }}>
                    SIZ
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: "#22C55E" }}>
                    {myProgress.correct}
                  </div>
                </div>
              </div>

              {/* Progress counter */}
              <div
                style={{
                  background: "rgba(31,45,69,.5)",
                  padding: "4px 12px",
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 800,
                  color: "#9ca3af",
                }}>
                {current + 1} / {totalQ}
              </div>

              {/* Opponent */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexDirection: "row-reverse",
                }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    overflow: "hidden",
                    background: "#EF4444",
                    flexShrink: 0,
                  }}>
                  <img
                    src={photoUrl(opponentPhoto)}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => {
                      e.currentTarget.src = DEFAULT_IMG;
                    }}
                  />
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 9, color: "#6b7280", fontWeight: 800 }}>
                    DO'ST
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: "#EF4444" }}>
                    {opponentProgress.correct}
                  </div>
                </div>
              </div>
            </div>

            {/* Progress bars */}
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <div
                style={{
                  flex: 1,
                  height: 4,
                  background: "#1f2d45",
                  borderRadius: 99,
                  overflow: "hidden",
                }}>
                <div
                  style={{
                    height: "100%",
                    width: `${progressPct}%`,
                    background: "#3B82F6",
                    borderRadius: 99,
                    transition: "width .4s ease",
                  }}
                />
              </div>
              <span style={{ fontSize: 10, color: "#4b5563", fontWeight: 700 }}>
                VS
              </span>
              <div
                style={{
                  flex: 1,
                  height: 4,
                  background: "#1f2d45",
                  borderRadius: 99,
                  overflow: "hidden",
                }}>
                <div
                  style={{
                    height: "100%",
                    width: `${oppProgressPct}%`,
                    background: "#EF4444",
                    borderRadius: 99,
                    transition: "width .4s ease",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            maxWidth: 620,
            margin: "0 auto",
            padding: "100px 16px 24px",
          }}>
          {/* Opponent finished banner */}
          {opponentFinishedMsg && (
            <div
              style={{
                background: "rgba(245,158,11,.1)",
                border: "1px solid rgba(245,158,11,.3)",
                borderRadius: 12,
                padding: "10px 16px",
                marginBottom: 12,
                color: "#fbbf24",
                fontSize: 13,
                fontWeight: 700,
                textAlign: "center",
              }}>
              ⚡ {opponentFinishedMsg}
            </div>
          )}

          {/* Question text */}
          <div
            style={{
              background: "#111827",
              border: "1px solid #1f2d45",
              borderRadius: 16,
              padding: "16px",
              marginBottom: 12,
            }}>
            <div
              style={{
                color: "#6b7280",
                fontSize: 11,
                fontWeight: 800,
                marginBottom: 8,
                textAlign: "center",
              }}>
              SAVOL {current + 1} / {totalQ}
            </div>
            <p
              style={{
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.6,
                color: "#f3f4f6",
                textAlign: "center",
              }}>
              {getQ(q)}
            </p>
          </div>

          {/* Question image */}
          <div
            style={{
              borderRadius: 16,
              overflow: "hidden",
              border: "1px solid #1f2d45",
              marginBottom: 14,
              background: "#000",
            }}>
            <img
              src={q.photo_url ? `${IMG_BASE}/${q.photo_url}` : DEFAULT_IMG}
              alt="Savol"
              style={{ width: "100%", maxHeight: 200, objectFit: "contain" }}
              onError={(e) => (e.currentTarget.src = DEFAULT_IMG)}
            />
          </div>

          {/* Choices */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {q.choices.map((choice, idx) => {
              const isSelected = answered[current] === choice.index;
              const isCorrectChoice =
                hasAnswered && choice.index === currentResult?.correctIndex;
              const isWrongSelected =
                isSelected && currentResult && !currentResult.isCorrect;

              let bg = "#111827";
              let border = "#1f2d45";
              let color = "#e5e7eb";

              if (hasAnswered && currentResult) {
                if (isCorrectChoice) {
                  bg = "rgba(34,197,94,0.2)";
                  border = "#22C55E";
                  color = "#86efac";
                } else if (isWrongSelected) {
                  bg = "rgba(239,68,68,0.2)";
                  border = "#EF4444";
                  color = "#fca5a5";
                } else {
                  bg = "rgba(255,255,255,.02)";
                  color = "#4b5563";
                }
              }

              return (
                <button
                  key={idx}
                  disabled={hasAnswered}
                  onClick={() => handleAnswer(choice.index)}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    background: bg,
                    border: `1px solid ${border}`,
                    borderRadius: 14,
                    color,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    cursor: hasAnswered ? "default" : "pointer",
                    transition: "all .15s",
                    fontWeight: 600,
                    fontSize: 14,
                    textAlign: "left",
                  }}>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      flexShrink: 0,
                      background: "rgba(255,255,255,.06)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 900,
                      border: "1px solid rgba(255,255,255,.1)",
                    }}>
                    {String.fromCharCode(65 + idx)}
                  </div>
                  {getChoice(choice)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // EKRAN 5 — WAITING FINISH (birinchi tugatganda)
  // ════════════════════════════════════════════════════════════════════════════
  if (step === "waiting_finish") {
    const totalQ = questions.length;
    const oppPct =
      totalQ > 0 ? (opponentProgress.answered / totalQ) * 100 : 0;

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0A0E1A",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          padding: "24px",
        }}>
        <div style={{ textAlign: "center", maxWidth: 360, width: "100%" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <h2 style={{ fontWeight: 900, fontSize: 22, marginBottom: 6 }}>
            Siz tugadingiz!
          </h2>
          <div style={{ color: "#22C55E", fontWeight: 900, fontSize: 32, marginBottom: 4 }}>
            {myProgress.correct} / {totalQ}
          </div>
          <div
            style={{
              color: "#9ca3af",
              fontSize: 14,
              marginBottom: 32,
            }}>
            To'g'ri javoblar
          </div>

          {/* Opponent progress */}
          <div
            style={{
              background: "#111827",
              border: "1px solid #1f2d45",
              borderRadius: 16,
              padding: "16px",
              marginBottom: 16,
            }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  overflow: "hidden",
                  flexShrink: 0,
                }}>
                <img
                  src={photoUrl(opponentPhoto)}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.currentTarget.src = DEFAULT_IMG;
                  }}
                />
              </div>
              <span style={{ color: "#9ca3af", fontSize: 13, fontWeight: 700 }}>
                {opponentName} hali yechmoqda...
              </span>
              <span style={{ marginLeft: "auto", color: "#EF4444", fontWeight: 900 }}>
                {opponentProgress.correct}/{opponentProgress.answered}
              </span>
            </div>

            <div
              style={{
                height: 6,
                background: "#1f2d45",
                borderRadius: 99,
                overflow: "hidden",
              }}>
              <div
                style={{
                  height: "100%",
                  width: `${oppPct}%`,
                  background: "#EF4444",
                  borderRadius: 99,
                  transition: "width .4s ease",
                }}
              />
            </div>
            <div
              style={{
                textAlign: "right",
                fontSize: 11,
                color: "#6b7280",
                marginTop: 4,
              }}>
              {opponentProgress.answered} / {totalQ}
            </div>
          </div>

          {opponentFinishedMsg && (
            <div
              style={{
                color: "#fbbf24",
                fontSize: 13,
                fontWeight: 700,
                background: "rgba(245,158,11,.1)",
                border: "1px solid rgba(245,158,11,.2)",
                borderRadius: 10,
                padding: "10px 16px",
              }}>
              ⚡ {opponentFinishedMsg}
            </div>
          )}

          {/* Spinner */}
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "3px solid rgba(59,130,246,.2)",
              borderTop: "3px solid #3B82F6",
              animation: "spin 1s linear infinite",
              margin: "24px auto 0",
            }}
          />
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // EKRAN 6 — RESULT
  // ════════════════════════════════════════════════════════════════════════════
  if (step === "result" && resultData) {
    const { isWinner, player1Score, player2Score } = resultData;
    const isDraw = player1Score === player2Score;

    let myScore: number;
    let oppScore: number;

    if (isDraw) {
      myScore = player1Score;
      oppScore = player2Score;
    } else if (isWinner) {
      myScore = Math.max(player1Score, player2Score);
      oppScore = Math.min(player1Score, player2Score);
    } else {
      myScore = Math.min(player1Score, player2Score);
      oppScore = Math.max(player1Score, player2Score);
    }

    const statusTitle =
      isWinner ? "G'ALABA! 🏆"
      : isDraw ? "DURANG! 🤝"
      : "MAG'LUBIYAT 😞";

    const statusColor =
      isWinner ? "#22C55E"
      : isDraw ? "#F59E0B"
      : "#EF4444";

    const statusBg =
      isWinner ? "rgba(34,197,94,.15)"
      : isDraw ? "rgba(245,158,11,.15)"
      : "rgba(239,68,68,.15)";

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0A0E1A",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 16px",
        }}>
        <div
          style={{
            width: "100%",
            maxWidth: 400,
            background: "#111827",
            border: "1px solid #1f2d45",
            borderRadius: 24,
            padding: "32px 24px",
            textAlign: "center",
          }}>
          <h2
            style={{
              fontWeight: 900,
              fontSize: 26,
              color: statusColor,
              marginBottom: 8,
            }}>
            {statusTitle}
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginBottom: 28,
              marginTop: 20,
            }}>
            {/* Me */}
            <div
              style={{
                background: "rgba(34,197,94,.05)",
                border: "1px solid rgba(34,197,94,.2)",
                borderRadius: 16,
                padding: "16px",
              }}>
              <div style={{ color: "#22C55E", fontWeight: 900, fontSize: 32 }}>
                {myScore}
              </div>
              <div
                style={{
                  color: "#6b7280",
                  fontSize: 11,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  marginTop: 4,
                }}>
                SIZ
              </div>
            </div>

            {/* Opponent */}
            <div
              style={{
                background: statusBg,
                border: `1px solid ${statusColor}33`,
                borderRadius: 16,
                padding: "16px",
              }}>
              <div style={{ color: statusColor, fontWeight: 900, fontSize: 32 }}>
                {oppScore}
              </div>
              <div
                style={{
                  color: "#6b7280",
                  fontSize: 11,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  marginTop: 4,
                }}>
                {opponentName?.split(" ")[0] || "DO'ST"}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button
              onClick={() => {
                socketRef.current?.disconnect();
                navigate("/custom");
              }}
              style={{
                width: "100%",
                padding: "16px",
                background: "linear-gradient(135deg, #3B82F6, #8B5CF6)",
                border: "none",
                borderRadius: 16,
                color: "#fff",
                fontWeight: 900,
                fontSize: 15,
                cursor: "pointer",
              }}>
              👥 Qayta o'ynash
            </button>

            <button
              onClick={() => {
                socketRef.current?.disconnect();
                navigate("/dashboard");
              }}
              style={{
                width: "100%",
                padding: "14px",
                background: "transparent",
                border: "1px solid rgba(255,255,255,.1)",
                borderRadius: 16,
                color: "#9ca3af",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
              }}>
              🏠 Bosh menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fallback loading
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0A0E1A",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "3px solid rgba(59,130,246,.2)",
          borderTopColor: "#3B82F6",
          animation: "spin 1s linear infinite",
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default FriendDuelPage;
